import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AutomationTab = ({ onMessage, onSave }) => {
  // Review settings state
  const [reviewSettings, setReviewSettings] = useState({
    autoSendReviews: true,
    sendDelay: 24,
    reminderDelay: 72,
    maxReminders: 1, // Fixed to 1 reminder only
    emailEnabled: true,
    autoSendReminders: true
  });

  const [processing, setProcessing] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [reminderStats, setReminderStats] = useState(null);

  const handleReviewSettingChange = (key, value) => {
    setReviewSettings(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Auto-save to parent component
    if (onSave) {
      onSave({ [key]: value });
    }
  };

  const triggerReminderProcessing = async () => {
    setProcessing(true);
    try {
      // Add reminder processing logic here
      onMessage('success', 'Reminder processing initiated successfully!');
    } catch (error) {
      console.error('Reminder processing error:', error);
      onMessage('error', 'Failed to process reminders');
    } finally {
      setProcessing(false);
    }
  };

  const loadReminderStatistics = async () => {
    setLoadingStats(true);
    try {
      // Add reminder statistics loading logic here
      setReminderStats({
        period: "Last 30 days",
        totalRequests: 0,
        completedReviews: 0,
        pendingReminders: 0,
        completionRate: "0%"
      });
    } catch (error) {
      console.error('Error loading reminder statistics:', error);
      onMessage('error', 'Failed to load reminder statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Automation Settings */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Automation Settings
        </h3>
        
        <div className="space-y-4">
          {/* Auto-send toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-text-primary">
                Automatic Review Requests
              </label>
              <p className="text-sm text-text-secondary">
                Automatically send review requests after service completion
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={reviewSettings.autoSendReviews}
                onChange={(e) => handleReviewSettingChange('autoSendReviews', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Email enabled toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-text-primary">
                Email Notifications
              </label>
              <p className="text-sm text-text-secondary">
                Send review requests via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={reviewSettings.emailEnabled}
                onChange={(e) => handleReviewSettingChange('emailEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Send delay */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Send Delay (hours after service completion)
            </label>
            <Input
              type="number"
              min="1"
              max="168"
              value={reviewSettings.sendDelay}
              onChange={(e) => handleReviewSettingChange('sendDelay', parseInt(e.target.value))}
              placeholder="24"
            />
            <p className="text-sm text-text-secondary mt-1">
              Recommended: 24 hours (1-168 hours allowed)
            </p>
          </div>

          {/* Reminder delay */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Reminder Delay (hours after first email)
            </label>
            <Input
              type="number"
              min="24"
              max="336"
              value={reviewSettings.reminderDelay}
              onChange={(e) => handleReviewSettingChange('reminderDelay', parseInt(e.target.value))}
              placeholder="72"
            />
            <p className="text-sm text-text-secondary mt-1">
              Recommended: 72 hours (24-336 hours allowed)
            </p>
          </div>

          {/* Max reminders - Fixed to 1 */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Maximum Reminders
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-sm text-gray-600 font-medium">1 reminder</span>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              Fixed to send exactly 1 reminder email per review request
            </p>
          </div>
        </div>
      </div>

      {/* Reminder Automation */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Reminder Automation
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          Automatically send reminder emails to customers who haven't responded to review requests.
        </p>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-text-primary">
                Automatic Reminders
              </label>
              <p className="text-sm text-text-secondary">
                Send reminder emails based on delay settings
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={reviewSettings.autoSendReminders}
                onChange={(e) => handleReviewSettingChange('autoSendReminders', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={triggerReminderProcessing}
              disabled={processing}
              variant="outline"
              className="flex-1"
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="Send" size={16} className="mr-2" />
                  Send Due Reminders
                </>
              )}
            </Button>
            
            <Button
              onClick={loadReminderStatistics}
              disabled={loadingStats}
              variant="ghost"
            >
              {loadingStats ? (
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Icon name="RefreshCw" size={16} />
              )}
            </Button>
          </div>

          {reminderStats && (
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Reminder Statistics ({reminderStats.period})</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Total Requests</p>
                  <p className="text-lg font-semibold text-gray-900">{reminderStats.totalRequests}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-lg font-semibold text-green-600">{reminderStats.completedReviews}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending Reminders</p>
                  <p className="text-lg font-semibold text-yellow-600">{reminderStats.pendingReminders}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Completion Rate</p>
                  <p className="text-lg font-semibold text-blue-600">{reminderStats.completionRate}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Business Information Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Business Information</h4>
            <p className="text-blue-800 text-sm mb-2">
              Configure your business information in the Business Settings tab.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationTab; 